import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { PermissionRequired } from '@/components/PermissionRequired';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import type { Team, TeamMember } from '../types';
import { Users, Plus, Trash2, UserPlus, Pencil } from 'lucide-react';

export default function TeamsPage() {
  const { t } = useTranslation();
  const { data: teams, loading, error, refetch } = useApi<Team[]>(() => api('/teams'), []);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  // Edit team
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');

  // Members dialog
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersTeam, setMembersTeam] = useState<Team | null>(null);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState<'MEMBER' | 'LEAD'>('MEMBER');
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    async function f() {
      try { const d = await api<{ id: string; name: string }[]>('/users'); setUsersList(d || []); } catch {}
    }
    f();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(''); setBusy(true);
    try { await api('/teams', { method: 'POST', body: JSON.stringify({ name }) }); setName(''); setOpen(false); refetch(); }
    catch (err: any) { setFormError(err.message); }
    finally { setBusy(false); }
  };

  const openEdit = (team: Team) => {
    setEditId(team.id);
    setEditName(team.name);
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setEditError(''); setEditBusy(true);
    try { await api(`/teams/${editId}`, { method: 'PATCH', body: JSON.stringify({ name: editName }) }); setEditOpen(false); setEditId(null); refetch(); }
    catch (err: any) { setEditError(err.message); }
    finally { setEditBusy(false); }
  };

  const handleDelete = async (id: string) => { try { await api(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify({ active: false }) }); refetch(); } catch {} };

  const addMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!membersTeam || !memberUserId) return;
    setMemberBusy(true); setMemberError('');
    try {
      await api(`/teams/${membersTeam.id}/members`, { method: 'POST', body: JSON.stringify({ userId: memberUserId, role: memberRole }) });
      setMemberUserId(''); setMemberError(''); refetch();
    }
    catch (err: any) { setMemberError(err.message); }
    finally { setMemberBusy(false); }
  };

  const removeMember = async (teamId: string, userId: string) => {
    try { await api(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }); refetch(); } catch {}
  };

  const columns = [
    { key: 'name', header: t('name') || 'Name' },
    { key: 'status', header: t('status') || 'Status', render: (tg: Team) => <Badge variant={tg.active ? 'success' : 'secondary'}>{tg.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: '', className: 'w-32', render: (tg: Team) => (
      <div className="flex items-center gap-1">
        <Button data-testid="members-team-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]" onClick={() => { setMembersTeam(tg); setMembersOpen(true); }} aria-label="Members"><UserPlus className="h-4 w-4" /></Button>
        <Button data-testid="edit-team-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]" onClick={() => openEdit(tg)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
        <Button data-testid="delete-team-button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700" onClick={() => handleDelete(tg.id)} aria-label={t('delete') as string}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ) },
  ];

  const renderMembers = (tg: Team) => {
    const members = tg.members || [];
    return (
      <div className="flex flex-wrap gap-2">
        {members.map((m: TeamMember) => (
          <Badge key={m.id} variant="outline" className="flex items-center gap-2 px-2 py-1">
            <Avatar className="h-5 w-5"><AvatarFallback>{(m.user?.name || m.userId).charAt(0).toUpperCase()}</AvatarFallback></Avatar>
            <span className="text-xs">{m.user?.name || m.userId}</span>
          </Badge>
        ))}
        {members.length === 0 && <span className="text-xs text-[var(--color-muted-foreground)]">No members</span>}
      </div>
    );
  };

  const mobileRender = (tg: Team) => (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Users className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0"><div className="text-sm font-medium">{tg.name}</div></div>
        <Badge variant={tg.active ? 'success' : 'secondary'}>{tg.active ? 'Active' : 'Inactive'}</Badge>
      </div>
      {renderMembers(tg)}
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => { setMembersTeam(tg); setMembersOpen(true); }}><UserPlus className="mr-1 h-3.5 w-3.5" /> Members</Button>
        <Button variant="ghost" size="sm" onClick={() => openEdit(tg)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(tg.id)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
      </div>
    </div>
  );

  return (
    <div className="animate-in">
      <PageHeader title={t('teams') || 'Teams'}>
        <Dialog open={open} onOpenChange={setOpen}>
          <PermissionRequired pageKey="teams" minLevel="READWRITE">
            <DialogTrigger asChild><Button data-testid="create-team-button" size="sm"><Plus className="mr-2 h-4 w-4" /> New</Button></DialogTrigger>
          </PermissionRequired>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Team</DialogTitle><DialogDescription>Add a new team.</DialogDescription></DialogHeader>
            <form id="team-form" onSubmit={handleCreate} className="space-y-4 py-2">
              {formError && <Badge variant="destructive" className="w-full justify-center">{formError}</Badge>}
              <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={e=>setName(e.target.value)} required /></div>
            </form>
            <DialogFooter><Button data-testid="save-team-button" type="submit" form="team-form" loading={busy} disabled={busy}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <Badge variant="destructive" className="mb-4">{error}</Badge>}
      {loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <DataTable
          columns={columns}
          data={teams || []}
          keyExtractor={(tg) => tg.id}
          mobileRender={mobileRender}
          emptyState={<EmptyState icon={Users} title={t('noData') || 'No teams'} description="Create your first team." action={<Button size="sm" onClick={()=>setOpen(true)}><Plus className="mr-2 h-4 w-4" /> New Team</Button>} />}
        />
      )}
      {/* Edit team dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Team</DialogTitle><DialogDescription>Update team name.</DialogDescription></DialogHeader>
          <form id="edit-team-form" onSubmit={handleEdit} className="space-y-4 py-2">
            {editError && <Badge variant="destructive" className="w-full justify-center">{editError}</Badge>}
            <div className="space-y-2"><Label>Name</Label><Input value={editName} onChange={e=>setEditName(e.target.value)} required /></div>
          </form>
          <DialogFooter><Button data-testid="save-edit-team-button" type="submit" form="edit-team-form" loading={editBusy} disabled={editBusy}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Members dialog */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Manage Members</DialogTitle><DialogDescription>{membersTeam?.name}</DialogDescription></DialogHeader>
          <form onSubmit={addMember} className="flex flex-wrap items-end gap-3 py-2">
            <div className="w-full max-w-xs space-y-2"><Label>User</Label>
              <Select value={memberUserId} onValueChange={setMemberUserId} required>
                <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{usersList
                  .filter(u => !(membersTeam?.members || []).find((m: TeamMember) => m.userId === u.id))
                  .map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-2"><Label>Role</Label>
              <Select value={memberRole} onValueChange={(v) => setMemberRole(v as 'MEMBER' | 'LEAD')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MEMBER">Member</SelectItem><SelectItem value="LEAD">Lead</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="add-member-button" type="submit" loading={memberBusy} disabled={memberBusy}><Plus className="mr-2 h-4 w-4" /> Add</Button>
          </form>
          {memberError && <Badge variant="destructive" className="mb-2">{memberError}</Badge>}
          <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
            {(membersTeam?.members || []).map((m: TeamMember) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] px-3 py-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px]">{(m.user?.name || m.userId).charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                  <div className="text-sm">{m.user?.name || m.userId}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={m.role === 'LEAD' ? 'default' : 'secondary'} className="text-xs">{m.role}</Badge>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => membersTeam && removeMember(membersTeam.id, m.userId)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            {(membersTeam?.members || []).length === 0 && (
              <EmptyState icon={Users} title="No members" description="Add users to this team." />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
